import React, { useEffect, useMemo, useState } from 'react'
import { Button, Form, Select, notification } from 'antd'
import { Alert } from '@surecompanies/core_components'
import { EditOutlined, SaveOutlined, WarningOutlined } from '@ant-design/icons'
import { isNil } from 'lodash'
import {
  useBillingProfilesQuery,
  useCreateBillingProfileMutation,
  useUpdateBillingProfileMutation,
} from '../../api/client-billing-profile-api-client'
import {
  BillingProfileStatus,
  PaymentType,
} from '../ClientBillingProfileManagement/types'
import {
  DrawerEmailChip,
  DrawerEmailList,
  DrawerField,
  DrawerFieldLabel,
  DrawerFieldValue,
  DrawerNoEmailWarning,
  DrawerPaymentPill,
  DrawerSectionTitle,
  DrawerSectionTitleText,
} from './InvoiceDeliveryDrawer.styles'

interface EmailFormValues {
  toEmails: string[]
  ccEmails: string[]
  paymentType: PaymentType
}

interface ApiFieldError {
  field: string
  errors: string[]
}

const FIELD_MAP: Record<string, string> = {
  toEmails: 'toEmails',
  ccEmails: 'ccEmails',
  paymentType: 'paymentType',
}

interface EmailRecipientsSectionProps {
  clientId: string
  clientName: string | null
  open: boolean
}

const EmailRecipientsSection: React.FC<EmailRecipientsSectionProps> = ({
  clientId,
  clientName,
  open,
}) => {
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [form] = Form.useForm<EmailFormValues>()

  const { data: profileData, isLoading: profileLoading } =
    useBillingProfilesQuery(
      { clientPublicKey: clientId, limit: 1 },
      { enabled: open && clientId.length > 0 }
    )

  const billingProfile = useMemo(() => {
    if (!profileData?.data) return null
    const activeMatch = profileData.data.find(
      (r) => r.status === BillingProfileStatus.Active
    )
    return activeMatch ?? profileData.data[0] ?? null
  }, [profileData])

  useEffect(() => {
    if (isEditingEmail) {
      form.setFieldsValue({
        toEmails: billingProfile?.toEmails ?? [],
        ccEmails: billingProfile?.ccEmails ?? [],
        paymentType: billingProfile?.paymentType ?? undefined,
      })
    }
  }, [isEditingEmail, billingProfile, form])

  useEffect(() => {
    if (!open) setIsEditingEmail(false)
  }, [open])

  const applyApiErrors = (error: unknown) => {
    const data = (
      error as {
        response?: {
          data?: { message?: ApiFieldError[] | string; error?: string }
        }
      }
    )?.response?.data
    const fieldErrors: ApiFieldError[] =
      !isNil(data) && Array.isArray(data.message) ? data.message : []

    if (fieldErrors.length > 0) {
      form.setFields(
        fieldErrors.map(({ field, errors }) => ({
          name: (FIELD_MAP[field] ?? field) as keyof EmailFormValues,
          errors,
        }))
      )
    } else {
      const description =
        typeof data?.message === 'string'
          ? data.message
          : (data?.error ?? 'An unexpected error occurred')
      notification.error({
        message: 'Failed to save email config',
        description,
      })
    }
  }

  const createMutation = useCreateBillingProfileMutation({
    onSuccess: () => {
      notification.success({ message: 'Email config created' })
      setIsEditingEmail(false)
    },
    onError: applyApiErrors,
  })

  const updateMutation = useUpdateBillingProfileMutation({
    onSuccess: () => {
      notification.success({ message: 'Email config updated' })
      setIsEditingEmail(false)
    },
    onError: applyApiErrors,
  })

  const isEmailSaving = createMutation.isPending || updateMutation.isPending

  const handleEmailSave = async () => {
    let values: EmailFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    if (billingProfile) {
      updateMutation.mutate({
        id: billingProfile.id,
        body: {
          toEmails: values.toEmails,
          ccEmails: values.ccEmails,
          paymentType: values.paymentType,
        },
      })
    } else {
      if (!clientName || !clientId) {
        notification.error({
          message: 'Unmatched company',
          description:
            'This delivery must be matched to a known company before setting up an email config. ' +
            'Use the "Match Company" section above.',
        })
        return
      }
      createMutation.mutate({
        clientName,
        clientPublicKey: clientId,
        toEmails: values.toEmails,
        ccEmails: values.ccEmails,
        paymentType: values.paymentType,
      })
    }
  }

  const hasEmailConfig = !isNil(billingProfile)
  const isEmailActive =
    hasEmailConfig && billingProfile.status === BillingProfileStatus.Active

  return (
    <>
      <DrawerSectionTitle>
        <DrawerSectionTitleText>Email recipients</DrawerSectionTitleText>
        {!profileLoading && !isEditingEmail && (
          <button
            onClick={() => setIsEditingEmail(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#01A0A8',
              fontSize: 12,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: 0,
              letterSpacing: 0,
              textTransform: 'none',
            }}
          >
            <EditOutlined />
            {hasEmailConfig ? 'Edit' : 'Add'}
          </button>
        )}
      </DrawerSectionTitle>

      {profileLoading && (
        <DrawerFieldValue style={{ color: '#9ca3af' }}>
          Loading email config...
        </DrawerFieldValue>
      )}

      {/* ---- Edit mode ---- */}
      {!profileLoading && isEditingEmail && (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="toEmails"
            label={
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                TO Emails *
              </span>
            }
            rules={[
              {
                required: true,
                message: 'At least one TO email is required',
              },
            ]}
            style={{ marginBottom: 4 }}
          >
            <Select
              mode="tags"
              tokenSeparators={[',', ' ', ';']}
              placeholder="billing@company.com"
              open={false}
              size="small"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>
            Press Enter, comma, or semicolon after each address
          </div>

          <Form.Item
            name="ccEmails"
            label={
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                CC Emails
              </span>
            }
            style={{ marginBottom: 4 }}
          >
            <Select
              mode="tags"
              tokenSeparators={[',', ' ', ';']}
              placeholder="cc@company.com"
              open={false}
              size="small"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>
            Press Enter, comma, or semicolon after each address
          </div>

          <Form.Item
            name="paymentType"
            label={
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                Payment Type *
              </span>
            }
            rules={[{ required: true, message: 'Payment type is required' }]}
            style={{ marginBottom: 12 }}
          >
            <Select size="small" placeholder="Select payment type">
              <Select.Option value="WIRE">Wire</Select.Option>
              <Select.Option value="STRIPE">Stripe</Select.Option>
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              htmlType="button"
              size="small"
              onClick={handleEmailSave}
              loading={isEmailSaving}
              icon={<SaveOutlined />}
            >
              Save
            </Button>
            <Button
              htmlType="button"
              size="small"
              onClick={() => setIsEditingEmail(false)}
              disabled={isEmailSaving}
            >
              Cancel
            </Button>
          </div>
        </Form>
      )}

      {/* ---- Read mode ---- */}
      {!profileLoading && !isEditingEmail && (
        <>
          {!hasEmailConfig && (
            <DrawerNoEmailWarning>
              <WarningOutlined style={{ marginRight: 6, color: '#f59e0b' }} />
              No email recipients configured. Click <strong>Add</strong> above
              to set up.
            </DrawerNoEmailWarning>
          )}

          {hasEmailConfig && !isEmailActive && (
            <Alert
              type="warning"
              message="Email config exists but is inactive"
              description="This company's billing profile is currently deactivated. Activate it in Billing Profiles before approving."
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          {hasEmailConfig && (
            <>
              <DrawerField>
                <DrawerFieldLabel>TO</DrawerFieldLabel>
                <DrawerFieldValue>
                  {billingProfile.toEmails.length === 0 ? (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  ) : (
                    <DrawerEmailList>
                      {billingProfile.toEmails.map((email) => (
                        <DrawerEmailChip key={email} $variant="to">
                          {email}
                        </DrawerEmailChip>
                      ))}
                    </DrawerEmailList>
                  )}
                </DrawerFieldValue>
              </DrawerField>

              <DrawerField>
                <DrawerFieldLabel>CC</DrawerFieldLabel>
                <DrawerFieldValue>
                  {(billingProfile.ccEmails ?? []).length === 0 ? (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  ) : (
                    <DrawerEmailList>
                      {billingProfile.ccEmails.map((email) => (
                        <DrawerEmailChip key={email} $variant="cc">
                          {email}
                        </DrawerEmailChip>
                      ))}
                    </DrawerEmailList>
                  )}
                </DrawerFieldValue>
              </DrawerField>

              <DrawerField>
                <DrawerFieldLabel>Payment</DrawerFieldLabel>
                <DrawerFieldValue>
                  {billingProfile.paymentType ? (
                    <DrawerPaymentPill $type={billingProfile.paymentType}>
                      {billingProfile.paymentType === PaymentType.WIRE
                        ? 'Wire'
                        : 'Stripe'}
                    </DrawerPaymentPill>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </DrawerFieldValue>
              </DrawerField>
            </>
          )}
        </>
      )}
    </>
  )
}

export { EmailRecipientsSection }
